import { isNil, pathOr, omit } from 'ramda'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import capitalize from 'lodash.capitalize'

import MedicalHistory from 'model/person/medicalHistory'
import Upload from 'model/upload/upload'
import User from 'model/person/user'
import Person from 'model/person/person'
import InsurancePlan from 'model/insurancePlans/insurancePlan'
import Location from 'model/person/locations/location'
import CustomLocation from 'model/person/locations/customLocation'
import Organization from 'model/organizations/organization'
import {
  getDateFormat,
  getDateAndTimeFormat,
  getTimeFormat
} from 'utils/helpers/dateHelper'
import { initArray, initArrayWithNodeExtract } from 'model/helpers'

const PRIMARY_INSURANCE = 'main'

const CORE_PRODUCT_FLAG = 'core'
const PBM_PRODUCT_FLAG = 'pbm'
const PRODUCT_FLAG_DESCRIPTION = {
  core: { text: 'NAV ONLY', color: '#059669' },
  pbm: { text: 'PBM ONLY', color: '#F2B649' },
  joint: { text: 'JOINT', color: '#4085F1' }
}

class Member extends Person {
  constructor(init) {
    super(init)
    // console.log('> Member constructor -', init)
    if (isNil(init)) return undefined

    this._middle_name = init.middle_name
    this._active = init.active
    this._age = init.age
    this._address = init.address
    this._address_details = new Location(init.address_details)
    this._deactivation_details = init.deactivation_details
    this._deactivated_at = init.deactivated_at
    this._flags_ccwmp_enrollment = init.flags_ccwmp_enrollment
    this._flags_ccdp_enrollment = init.flags_ccdp_enrollment
    this._can_send_invitation = init.can_send_invitation
    this._carrier_display_name = init.carrier_display_name
    this._cardholder_id = init.cardholder_id
    this._sms_enabled = init.sms_enabled
    this._card_photos = initArrayWithNodeExtract(
      init,
      ['card_photos', 'edges'],
      Upload
    )
    this._dependents = pathOr([], ['dependents', 'edges'], init)
    this._dob = init.dob
    this._email = init.email
    this._group_number = init.group_number
    this._high_risk = init.high_risk
    this._hipaa_auth_required = init.hipaa_auth_required
    this._hipaa_expires_at = init.hipaa_expires_at
    this._hipaa_expired = init.hipaa_expired
    // Duct tape till we have a model for interactions
    this._interactions = omit(
      ['__typename'],
      pathOr({}, ['interactions'], init)
    )
    this._insurance_medical_plan = new InsurancePlan(
      init.insurance_medical_plan
    )
    this._last_interaction = init.last_interaction
    this._locale = init.locale
    this._from_rippling = init.from_rippling
    this._locations = initArray(init, ['locations'], CustomLocation)
    this._medical_history = new MedicalHistory(init.medical_history)
    this._member_id = init.member_id
    this._mfa_email = init.mfa_email
    this._mfa_phone_number = init.mfa_phone_number
    this._individual_ssn_scrubbed = init.individual_ssn_scrubbed
    this._insurance_identifiers = init.insurance_identifiers
    this._navigator_notes = init.navigator_notes
    this._onboarded = init.onboarded
    this._open_tickets_count = init.open_tickets_count
    this._organizational_flags = init.organizational_flags
    this._organization = new Organization(init.organization)
    this._organization_live = init.organization_live
    this._organization_name = init.organization_name
    this._organization_splash_screen_image =
      init.organization_splash_screen_image
    // Duct tape till we have a model for payments_v2
    this._payments_v2 = omit(['__typename'], pathOr({}, ['payments_v2'], init))
    this._phone_number = init.phone_number
    this._phone_number_us = init.phone_number_us
    this._pin_ticket = init.pin_ticket
    this._pin_upload = init.pin_upload
    this._preferred_email = init.preferred_email
    this._preferred_name = init.preferred_name
    this._preferred_phone_number = init.preferred_phone_number
    this._preferred_contact_method = init.preferred_contact_method
    this._preferred_language = init.preferred_language
    this._primary = init.primary
    this._product_flags = init.product_flags
    this._provider_uuid = init.provider_uuid
    this._relation_to_insured = init.relation_to_insured
    this._register_status = init.register_status
    this._shared_id = init.shared_id
    this._third_party_admin_name = init.third_party_admin_name
    this._telemedicine = init.telemedicine
    this._test = init.test
    this._time_zone_name = init.time_zone_name
    this._vip = init.vip
    this._user = new User(init.user)
    this._work_zip = init.work_zip
  }

  static shape = {
    ...Person.shape,
    active: PropTypes.bool,
    age: PropTypes.number,
    id: PropTypes.string.isRequired,
    address: PropTypes.string,
    address_details: PropTypes.instanceOf(Location),
    can_send_invitation: PropTypes.bool,
    carrier_display_name: PropTypes.string,
    cardholder_id: PropTypes.string,
    sms_enabled: PropTypes.bool,
    card_photos: PropTypes.arrayOf(PropTypes.instanceOf(Upload)),
    deactivation_details: PropTypes.object,
    deactivated_at: PropTypes.string,
    dependents: PropTypes.array,
    dob: PropTypes.string,
    email: PropTypes.string,
    flags_ccwmp_enrollment: PropTypes.string,
    flags_ccdp_enrollment: PropTypes.string,
    from_rippling: PropTypes.bool,
    group_number: PropTypes.string,
    high_risk: PropTypes.bool,
    hipaa_auth_required: PropTypes.bool,
    hipaa_expires_at: PropTypes.string,
    hipaa_expired: PropTypes.bool,
    interactions: PropTypes.object,
    insurance_identifiers: PropTypes.object,
    insurance_medical_plan: PropTypes.instanceOf(InsurancePlan),
    last_interaction: PropTypes.string,
    locale: PropTypes.string,
    locations: PropTypes.array,
    medical_history: PropTypes.instanceOf(MedicalHistory),
    member_id: PropTypes.string,
    mfa_email: PropTypes.string,
    mfa_phone_number: PropTypes.string,
    individual_ssn_scrubbed: PropTypes.string,
    navigator_notes: PropTypes.string,
    onboarded: PropTypes.bool,
    open_tickets_count: PropTypes.number,
    organizational_flags: PropTypes.array,
    organization: PropTypes.instanceOf(Organization),
    organization_live: PropTypes.bool,
    organization_name: PropTypes.string,
    organization_splash_screen_image: PropTypes.string,
    payments_v2: PropTypes.object,
    phone_number: PropTypes.string,
    phone_number_us: PropTypes.string,
    pin_ticket: PropTypes.object,
    pin_upload: PropTypes.object,
    preferred_email: PropTypes.string,
    preferred_name: PropTypes.string,
    preferred_phone_number: PropTypes.string,
    preferred_contact_method: PropTypes.string,
    preferred_language: PropTypes.string,
    primary: PropTypes.object,
    product_flags: PropTypes.array,
    provider_uuid: PropTypes.string,
    relation_to_insured: PropTypes.string,
    register_status: PropTypes.string,
    shared_id: PropTypes.string,
    third_party_admin_name: PropTypes.string,
    telemedicine: PropTypes.bool,
    test: PropTypes.bool,
    time_zone_name: PropTypes.string,
    vip: PropTypes.bool,
    user: PropTypes.instanceOf(User),
    work_zip: PropTypes.string
  }

  // Getters for class fields
  get active() {
    return this._active
  }

  get age() {
    return this._age
  }

  get address() {
    return this._address
  }

  get from_rippling() {
    return this._from_rippling
  }

  get address_details() {
    return this._address_details
  }

  get can_send_invitation() {
    return this._can_send_invitation
  }

  get carrier_display_name() {
    return this._carrier_display_name || 'N/A'
  }

  get cardholder_id() {
    return this._cardholder_id || ''
  }

  get sms_enabled() {
    return this._sms_enabled
  }

  get card_photos() {
    return this._card_photos
  }

  get flags_ccwmp_enrollment() {
    return this._flags_ccwmp_enrollment
  }

  get flags_ccdp_enrollment() {
    return this._flags_ccdp_enrollment
  }

  get deactivation_details() {
    return this._deactivation_details || {}
  }

  get deactivated_at() {
    return this._deactivated_at || ''
  }

  get dependents() {
    return this._dependents || []
  }

  get dob() {
    return this._dob
  }

  get email() {
    return this._email
  }

  get group_number() {
    return this._group_number
  }

  get hipaa_auth_required() {
    return this._hipaa_auth_required
  }

  get hipaa_expires_at() {
    return this._hipaa_expires_at
  }

  get hipaa_expired() {
    return this._hipaa_expired
  }

  get high_risk() {
    return this._high_risk
  }

  get interactions() {
    return this._interactions
  }

  get insurance_identifiers() {
    return this._insurance_identifiers
  }

  get insurance_medical_plan() {
    return this._insurance_medical_plan
  }

  get last_interaction() {
    return this._last_interaction
  }

  get locale() {
    return this._locale
  }

  get locations() {
    return this._locations || []
  }

  get medical_history() {
    return this._medical_history
  }

  get member_id() {
    return this._member_id
  }

  get mfa_email() {
    return this._mfa_email
  }

  get mfa_phone_number() {
    return this._mfa_phone_number
  }

  get individual_ssn_scrubbed() {
    const individualSsnScrubbed = this._individual_ssn_scrubbed
    const reg = /\W*/
    const securityTemplate = '**-***-'

    return individualSsnScrubbed
      ? individualSsnScrubbed.replace(reg, securityTemplate)
      : ''
  }

  get navigator_notes() {
    return this._navigator_notes
  }

  get onboarded() {
    return this._onboarded
  }

  get open_tickets_count() {
    return this._open_tickets_count
  }

  get organization() {
    return this._organization
  }

  get organization_live() {
    return this._organization_live
  }

  get organization_name() {
    return this._organization_name || ''
  }

  get organization_splash_screen_image() {
    return this._organization_splash_screen_image || ''
  }

  get organizational_flags() {
    return this._organizational_flags || []
  }

  get payments_v2() {
    return this._payments_v2
  }

  get phone_number() {
    return this._phone_number
  }

  get phone_number_us() {
    return this._phone_number_us
  }

  get pin_ticket() {
    return this._pin_ticket
  }

  get pin_upload() {
    return this._pin_upload
  }

  get preferred_email() {
    return this._preferred_email
  }

  get middle_name() {
    return this._middle_name
  }

  get getFullNameWithMiddle() {
    const middle = this._middle_name ? ` ${capitalize(this._middle_name)}` : ''
    return `${capitalize(this._first_name)}${middle} ${capitalize(
      this._last_name
    )}`
  }

  get preferred_name() {
    return this._preferred_name
  }

  get preferred_phone_number() {
    return this._preferred_phone_number
  }

  get preferred_contact_method() {
    return this._preferred_contact_method
  }

  get preferred_language() {
    return this._preferred_language
  }

  get primary() {
    return this._primary || {}
  }

  get product_flags() {
    return this._product_flags || []
  }

  get provider_uuid() {
    return this._provider_uuid
  }

  get relation_to_insured() {
    return this._relation_to_insured
  }

  get register_status() {
    return this._register_status
  }

  get shared_id() {
    return this._shared_id
  }

  get telemedicine() {
    return this._telemedicine
  }

  get test() {
    return this._test
  }

  get time_zone_name() {
    return this._time_zone_name
  }

  get third_party_admin_name() {
    return this._third_party_admin_name || ''
  }

  get vip() {
    return this._vip
  }

  get user() {
    return this._user
  }

  get work_zip() {
    return this._work_zip
  }

  // Calculable
  get getInfo() {
    const memberOrgName = ` (${this._organization.getFullName})`
    if (!this._dob) return memberOrgName

    return ` (${getDateFormat(this._dob)})${memberOrgName}`
  }

  get getCity() {
    return pathOr('N/A', ['address_details', 'city'], this)
  }

  get getSelectOption() {
    return {
      id: this.id,
      sharedId: this.shared_id,
      name: this.getFullName,
      info: this.getInfo,
      title: `${this.getFullName} ${this.getInfo}`
    }
  }

  get isPrimaryInsured() {
    return this.relation_to_insured === PRIMARY_INSURANCE
  }

  get isCore() {
    const item = this.product_flags.find(i => i.code === CORE_PRODUCT_FLAG)
    return !!item?.checked
  }

  get isPbm() {
    const item = this.product_flags.find(i => i.code === PBM_PRODUCT_FLAG)
    return !!item?.checked
  }

  get productFlagsDescription() {
    if (this.isCore && this.isPbm) return PRODUCT_FLAG_DESCRIPTION.joint
    if (this.isCore) return PRODUCT_FLAG_DESCRIPTION.core
    if (this.isPbm) return PRODUCT_FLAG_DESCRIPTION.pbm
    return false
  }

  get getFullNameWithPreferred() {
    return `${this.getFullName} 
    ${this._preferred_name ? `(${capitalize(this._preferred_name)})` : ''}`
  }

  get getDisplayEmail() {
    if (this.preferred_email) return `${this.preferred_email} (preferred)`
    return this.email
  }

  get getDisplayPhone() {
    if (this.preferred_phone_number)
      return `${this.preferred_phone_number} (preferred)`
    return this.phone_number_us
  }

  get getStatus() {
    const { active, register_status, last_interaction } = this
    return `${active ? 'Active' : 'Not active'}, 
    ${register_status}, 
    Last interaction: ${
      last_interaction
        ? getDateAndTimeFormat(last_interaction)
        : 'Not specified'
    }`
  }

  get getDateOfBirth() {
    const date = dayjs(this.dob)
    if (date.isValid()) return dayjs(this.dob).format('MM/DD/YYYY')

    return 'N/A'
  }

  get getZone() {
    const city = this.address_details?.city || ''
    const state = this.address_details?.state || ''

    return `${city}${city && state && ', '}${state}`
  }

  get getTimeZone() {
    const { time_zone_name = '' } = this
    if (!time_zone_name) return ''

    return time_zone_name && getTimeFormat(dayjs.utc().tz(time_zone_name))
  }

  get getFullLocation() {
    const { address_1, zip } = this.address_details || {}
    const zone = this.getZone
    const time = this.getTimeZone

    const stateZip = [zone, zip].filter(Boolean).join(', ')
    const parts = [address_1, stateZip].filter(Boolean)
    const address = parts.length ? parts.join(', ') : 'N/A'

    return [address, time].filter(Boolean).join(' ')
  }
}

export default Member
